import {
  BrTaxRegime,
  SellerEntityStatus,
  TaxIdValidator,
  UpsertSellerEntityInputSchema,
  type AsaasNfseConfig,
  type BrTaxRegime as BrTaxRegimeT,
  type SellerEntity,
  type SellerEntityStatus as SellerEntityStatusT,
  type UpsertSellerEntityInput,
} from '@etiquetachef/types';

/**
 * Estado do editor de entidade vendedora e a conversão dele para o input do
 * callable `adminUpsertSellerEntity`.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: o admin edita a `etiquetachef-br`
 * com todos os dados para cobrar e emitir nota).
 *
 * Por quê: o formulário guarda texto (o que o `Input` do Cloudscape devolve) e o
 * contrato quer número, `null` e blocos opcionais. Esta conversão é pura e
 * testada à parte (`sellerEntityForm.test.ts`), para a tela não carregar regra.
 *
 * Contrato: `inputFromForm` devolve o `safeParse` do
 * `UpsertSellerEntityInputSchema` (`@etiquetachef/types`) — o MESMO schema que
 * o servidor usa; a tela mostra o primeiro erro. Campo de texto vazio vira
 * `null` onde o schema aceita `null`. `br_fiscal` só vai com sede BR e algum
 * campo fiscal preenchido (`brFiscalEmpty`); cada NFS-e
 * (live/sandbox) só vai com o seu interruptor ligado.
 *
 * Usado por: `SellerEntityEditorPage` (`/billing/seller-entities/:id`).
 *
 * Dados: os campos espelham `SellerEntitySchema` (`schemas.ts`) e
 * `SellerEntityBrFiscalSchema`/`AsaasNfseConfigSchema` (`brFiscal.ts`).
 * Percentuais em pontos (2 = 2 %); teto anual digitado em unidades e gravado
 * em centavos.
 */

/** Uma NFS-e (live ou sandbox) no formulário. */
export type NfseForm = {
  enabled: boolean;
  service_description: string;
  observations: string;
  /**
   * Id e nome vêm SEMPRE juntos de um item da lista do Asaas (dropdown) —
   * nunca digitados. O `municipal_service_code` saiu do formulário em
   * 2026-10-07 (#756): o campo livre aceitou "02800 | 1.05" (o nome) como
   * código, e a nota iria com id e código juntos.
   */
  municipal_service_id: string;
  municipal_service_name: string;
  retainIss: boolean;
  iss: string;
  pis: string;
  cofins: string;
  csll: string;
  inss: string;
  ir: string;
};

/** O formulário inteiro (texto, como os `Input` devolvem). */
export type SellerEntityForm = {
  id: string;
  legal_name: string;
  trading_name: string;
  headquarter_country: string;
  tax_id: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  default_currency: string;
  cap_mode: 'UNLIMITED' | 'YEARLY_CAP';
  /** Teto anual em CENTS inteiros (o `CurrencyInput` já devolve cents); null = vazio. */
  annual_cap_amount_cents: number | null;
  /** Moeda do teto; preservada da entidade (vazio = a moeda padrão). */
  annual_cap_currency: string;
  stripe_account_id: string;
  paddle_account_id: string;
  status: SellerEntityStatusT;
  /** #756 — o PSP cuja conta é desta PJ; '' = não declarado (legado). */
  payment_provider: string;
  /** #756 — o modo dessa conta: 'live' | 'sandbox'; '' = os dois (legado). */
  payment_mode: string;
  notes: string;
  asaas_live_tokenization_enabled: boolean;
  // Fiscal BR
  municipal_registration: string;
  state_registration: string;
  cnae: string;
  tax_regime: BrTaxRegimeT;
  special_tax_regime: string;
  national_portal_tax_calculation_regime: string;
  nbs_code: string;
  fiscal_email: string;
  rps_serie: string;
  rps_number: string;
  lote_number: string;
  address_number: string;
  address_complement: string;
  district: string;
  ibge_city_code: string;
  nfse_live: NfseForm;
  nfse_sandbox: NfseForm;
};

/** NFS-e desligada, com descrição padrão e alíquotas zeradas. */
function emptyNfse(): NfseForm {
  return {
    enabled: false,
    service_description: 'Assinatura EtiquetaChef',
    observations: '',
    municipal_service_id: '',
    municipal_service_name: '',
    retainIss: false,
    iss: '0',
    pis: '0',
    cofins: '0',
    csll: '0',
    inss: '0',
    ir: '0',
  };
}

/** Config gravada da NFS-e de um modo → campos do formulário (ausente = desligada). */
function nfseForm(c: AsaasNfseConfig | null | undefined): NfseForm {
  if (!c) return emptyNfse();
  return {
    enabled: true,
    service_description: c.service_description,
    observations: c.observations,
    municipal_service_id: c.municipal_service_id ?? '',
    municipal_service_name: c.municipal_service_name,
    retainIss: c.taxes.retainIss,
    iss: pct(c.taxes.iss),
    pis: pct(c.taxes.pis),
    cofins: pct(c.taxes.cofins),
    csll: pct(c.taxes.csll),
    inss: pct(c.taxes.inss),
    ir: pct(c.taxes.ir),
  };
}

/**
 * Alíquota gravada (2.9) como o operador brasileiro escreve (`2,9`). A volta é
 * `num`, que aceita vírgula e ponto (#756, owner 2026-10-07: "pode-se digitar
 * 2,9, cuidado").
 */
export function pct(n: number): string {
  return String(n).replace('.', ',');
}

/** Formulário de entidade nova — o padrão é a PJ brasileira (o caso da #756). */
export function emptyForm(): SellerEntityForm {
  return {
    id: '',
    legal_name: '',
    trading_name: '',
    headquarter_country: 'BR',
    tax_id: '',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    default_currency: 'BRL',
    cap_mode: 'UNLIMITED',
    annual_cap_amount_cents: null,
    annual_cap_currency: '',
    stripe_account_id: '',
    paddle_account_id: '',
    status: SellerEntityStatus.PENDING,
    payment_provider: '',
    payment_mode: '',
    notes: '',
    asaas_live_tokenization_enabled: false,
    municipal_registration: '',
    state_registration: '',
    cnae: '',
    tax_regime: BrTaxRegime.SIMPLES_NACIONAL,
    special_tax_regime: '',
    national_portal_tax_calculation_regime: '',
    nbs_code: '',
    fiscal_email: '',
    rps_serie: '',
    rps_number: '',
    lote_number: '',
    address_number: '',
    address_complement: '',
    district: '',
    ibge_city_code: '',
    nfse_live: emptyNfse(),
    nfse_sandbox: emptyNfse(),
  };
}

/** Preenche o formulário com uma entidade gravada. */
export function formFromEntity(e: SellerEntity): SellerEntityForm {
  const f = e.br_fiscal ?? null;
  const base = emptyForm();
  return {
    ...base,
    id: e.id,
    legal_name: e.legal_name,
    trading_name: e.trading_name ?? '',
    headquarter_country: e.headquarter_country,
    tax_id: formatTaxIdInput(e.headquarter_country, e.tax_id),
    street: e.address.street,
    city: e.address.city,
    state: e.address.state ?? '',
    postal_code: e.address.postal_code,
    default_currency: e.default_currency,
    cap_mode: e.cap_mode,
    annual_cap_amount_cents: e.annual_cap_amount_cents,
    annual_cap_currency: e.annual_cap_currency ?? '',
    stripe_account_id: e.stripe_account_id ?? '',
    paddle_account_id: e.paddle_account_id ?? '',
    status: e.status,
    payment_provider: e.payment_provider ?? '',
    payment_mode: e.payment_mode ?? '',
    notes: e.notes ?? '',
    asaas_live_tokenization_enabled: e.asaas_live_tokenization_enabled === true,
    ...(f
      ? {
          municipal_registration: f.municipal_registration,
          state_registration: f.state_registration ?? '',
          cnae: f.cnae,
          tax_regime: f.tax_regime,
          special_tax_regime: f.special_tax_regime ?? '',
          national_portal_tax_calculation_regime: f.national_portal_tax_calculation_regime ?? '',
          nbs_code: f.nbs_code ?? '',
          fiscal_email: f.fiscal_email,
          rps_serie: f.rps_serie ?? '',
          rps_number: f.rps_number === null ? '' : String(f.rps_number),
          lote_number: f.lote_number === null ? '' : String(f.lote_number),
          address_number: f.address_number,
          address_complement: f.address_complement ?? '',
          district: f.district,
          ibge_city_code: f.ibge_city_code,
          nfse_live: nfseForm(f.nfse.live),
          nfse_sandbox: nfseForm(f.nfse.sandbox),
        }
      : {}),
  };
}

/** Texto aparado; vazio → `null`. */
function orNull(s: string): string | null {
  const v = s.trim();
  return v === '' ? null : v;
}

/**
 * Número digitado; vazio → `null`; inválido → `NaN` (o Zod recusa).
 * Aceita "2,5", "2.5", "100.000" e "100.000,50" (milhar com ponto, padrão
 * BR/IT). Auditoria #756 (baixo): só trocar a vírgula gravava "100.000" como 100.
 */
function num(s: string): number | null {
  let v = s.trim().replace(/\s/g, '');
  if (v === '') return null;
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(v)) v = v.replace(/\./g, '');
  return Number(v);
}

/**
 * O bloco fiscal BR está todo vazio (nenhum campo digitado, NFS-e desligadas)?
 *
 * Auditoria 2026-10-01 (#756): sem isto o editor sempre mandava `br_fiscal`, e
 * o schema exige CCM, CNAE e e-mail fiscal — a PJ não podia ser cadastrada
 * antes de o contador fornecer esses dados. Vazio → `br_fiscal: null`
 * (entidade sem dados fiscais ainda); qualquer campo preenchido → o bloco vai
 * inteiro e o schema aponta o que falta.
 */
function brFiscalEmpty(f: SellerEntityForm): boolean {
  const texts = [
    f.municipal_registration, f.state_registration, f.cnae, f.special_tax_regime,
    f.national_portal_tax_calculation_regime, f.nbs_code, f.fiscal_email, f.rps_serie,
    f.rps_number, f.lote_number, f.address_number, f.address_complement, f.district, f.ibge_city_code,
  ];
  return texts.every((t) => t.trim() === '') && !f.nfse_live.enabled && !f.nfse_sandbox.enabled;
}

/** Bloco NFS-e de um modo para o input; desligado → `null`. Alíquotas em % (2,9 → 2.9). */
function nfseInput(n: NfseForm): Record<string, unknown> | null {
  if (!n.enabled) return null;
  return {
    service_description: n.service_description.trim(),
    observations: n.observations.trim(),
    // Só o id (da lista do Asaas); o código nunca sai do formulário (#756).
    ...(orNull(n.municipal_service_id) ? { municipal_service_id: n.municipal_service_id.trim() } : {}),
    municipal_service_name: n.municipal_service_name.trim(),
    taxes: {
      retainIss: n.retainIss,
      iss: num(n.iss) ?? 0,
      pis: num(n.pis) ?? 0,
      cofins: num(n.cofins) ?? 0,
      csll: num(n.csll) ?? 0,
      inss: num(n.inss) ?? 0,
      ir: num(n.ir) ?? 0,
    },
  };
}

/**
 * Converte o formulário no input do callable, validado pelo schema do servidor.
 *
 * Origem: #756 — 2026-10-01 12:04 +0200 (f67d187c0, entidades vendedoras com dados fiscais BR).
 *
 * Por quê: validar no cliente com o MESMO `UpsertSellerEntityInputSchema` que
 * o `adminUpsertSellerEntity` usa faz a tela apontar cada campo errado antes
 * do envio, em vez de devolver um `invalid-argument` genérico.
 *
 * Contrato: função pura; devolve o `safeParse` (nunca lança). Normaliza:
 * texto aparado e vazio → `null`; CNAE e IBGE só dígitos; números com vírgula
 * ou milhar com ponto (`num`); teto anual só com `cap_mode: 'YEARLY_CAP'`
 * (moeda cai na padrão); `payment_mode` só com PSP; tokenização Asaas só no
 * BR; `br_fiscal` só no BR e só se algum campo fiscal foi digitado
 * (`brFiscalEmpty`), senão `null`.
 *
 * Usado por: `SellerEntityEditorPage.tsx` (`requestSave` para os erros por
 * campo; `save` para o input do callable); `sellerEntityForm.test.ts`.
 *
 * Arquitetura: o servidor (`apps/functions/src/admin/sellerEntities.ts`) valida
 * de novo e é quem grava `config/sellerEntities/items/{id}` — as Rules negam
 * escrita do cliente. Esta função não substitui aquela validação.
 *
 * Dados: saída = `UpsertSellerEntityInput` (`packages/types/src/schemas.ts`);
 * `annual_cap_amount_cents` em centavos; alíquotas da NFS-e em percentual.
 *
 * Falhas conhecidas:
 *  - 2026-10-01 (#756, auditoria): `br_fiscal` ia sempre, e o schema exigia
 *    CCM/CNAE/e-mail fiscal antes de o contador mandar — hoje `brFiscalEmpty`.
 *  - 2026-10-01 (#756, auditoria baixo): "100.000" virava 100 — hoje `num`.
 */
export function inputFromForm(
  f: SellerEntityForm,
  reason: string,
): ReturnType<typeof UpsertSellerEntityInputSchema.safeParse> {
  const isBr = f.headquarter_country === 'BR';
  const raw: Record<string, unknown> = {
    id: f.id.trim(),
    legal_name: f.legal_name.trim(),
    trading_name: orNull(f.trading_name),
    headquarter_country: f.headquarter_country.trim().toUpperCase(),
    tax_id: f.tax_id.trim(),
    address: {
      street: f.street.trim(),
      city: f.city.trim(),
      state: orNull(f.state),
      postal_code: f.postal_code.trim(),
    },
    default_currency: f.default_currency,
    cap_mode: f.cap_mode,
    annual_cap_amount_cents: f.cap_mode === 'YEARLY_CAP' ? f.annual_cap_amount_cents : null,
    annual_cap_currency: f.cap_mode === 'YEARLY_CAP' ? f.annual_cap_currency || f.default_currency : null,
    stripe_account_id: orNull(f.stripe_account_id),
    paddle_account_id: orNull(f.paddle_account_id),
    status: f.status,
    payment_provider: orNull(f.payment_provider),
    payment_mode: f.payment_provider ? orNull(f.payment_mode) : null,
    notes: orNull(f.notes),
    asaas_live_tokenization_enabled: isBr ? f.asaas_live_tokenization_enabled : false,
    br_fiscal: isBr && !brFiscalEmpty(f)
      ? {
          municipal_registration: f.municipal_registration.trim(),
          state_registration: orNull(f.state_registration),
          cnae: f.cnae.replace(/\D/g, ''),
          tax_regime: f.tax_regime,
          special_tax_regime: orNull(f.special_tax_regime),
          national_portal_tax_calculation_regime: orNull(f.national_portal_tax_calculation_regime),
          nbs_code: orNull(f.nbs_code),
          fiscal_email: f.fiscal_email.trim(),
          rps_serie: orNull(f.rps_serie),
          rps_number: num(f.rps_number),
          lote_number: num(f.lote_number),
          address_number: f.address_number.trim(),
          address_complement: orNull(f.address_complement),
          district: f.district.trim(),
          ibge_city_code: f.ibge_city_code.replace(/\D/g, ''),
          nfse: { live: nfseInput(f.nfse_live), sandbox: nfseInput(f.nfse_sandbox) },
        }
      : null,
    reason: reason.trim(),
  };
  return UpsertSellerEntityInputSchema.safeParse(raw);
}

/** O input já validado, para o callable. */
export type SellerEntityUpsert = UpsertSellerEntityInput;

/**
 * Erro do CNPJ quando a sede é BR (dígito verificador incluído); null se ok ou
 * se o país não é BR (fora do BR o identificador é livre, só o tamanho vale).
 *
 * Origem: #756 — 2026-10-01 (owner: "CNPJ com máscara se país BR").
 *
 * Por quê: o schema do servidor só exige 5..50 caracteres — um CNPJ com DV
 * errado passava e só quebrava na emissão da NFS-e e na conferência de
 * identidade da chave do Asaas (`pspSecrets.ts`, compara os dígitos).
 *
 * Usado por: `SellerEntityEditorPage` (errorText do campo e bloqueio do Salvar).
 */
export function taxIdError(f: SellerEntityForm): string | null {
  if (f.headquarter_country !== 'BR') return null;
  return TaxIdValidator.specFor('BR').validate(f.tax_id)?.message ?? null;
}

/**
 * Formata o CNPJ que o operador digita (`00.000.000/0000-00`, todos os
 * dígitos visíveis) quando a sede é BR; fora do BR devolve o texto como veio.
 * É pontuação de entrada, não ocultação — chamava `maskTaxId` até 2026-10-13
 * e foi renomeada na #939 (o gate `check-no-id-masking.mjs` proíbe máscara de
 * identificador, e o nome antigo confundia as duas coisas).
 *
 * Origem: #756 — 2026-10-01. O documento guarda o CNPJ formatado, como os
 * docs existentes; quem compara (identidade da chave) usa só os dígitos.
 *
 * Usado por: `SellerEntityEditorPage` (onChange do campo e carga do doc).
 */
export function formatTaxIdInput(country: string, raw: string): string {
  if (country !== 'BR') return raw;
  const spec = TaxIdValidator.specFor('BR');
  return spec.format(TaxIdValidator.cleanInput('BR', raw));
}

/**
 * Link do Google Maps para o endereço fiscal, para o operador conferir que o
 * endereço digitado existe. null enquanto não há rua nem cidade.
 *
 * Origem: #756 — 2026-10-01 (owner: "Endereço fiscal — link para google maps").
 * Usa a URL universal `maps/search/?api=1` (não exige chave de API).
 *
 * Usado por: `SellerEntityEditorPage` (cabeçalho da seção Endereço fiscal).
 */
export function mapsUrl(f: SellerEntityForm): string | null {
  if (f.street.trim() === '' && f.city.trim() === '') return null;
  const parts = [
    [f.street, f.address_number].map((x) => x.trim()).filter(Boolean).join(', '),
    f.district,
    f.city,
    f.state,
    f.postal_code,
    f.headquarter_country,
  ]
    .map((x) => x.trim())
    .filter(Boolean);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
}

/** Um problema de validação já traduzido para o campo da tela. */
export type FieldIssue = {
  /** Chave do campo na tela: a do formulário (`municipal_registration`) ou
   *  `nfse_live.<campo>` / `nfse_sandbox.<campo>` dentro de um bloco de NFS-e. */
  field: string;
  kind: 'required' | 'format' | 'custom' | 'invalid';
  /** Mensagem do schema quando é regra nossa (`refine`, já em pt-BR). */
  message: string;
};

/**
 * Converte os issues do Zod no campo da tela que cada um aponta.
 *
 * Origem: #756 — 2026-10-01 15:48 +0200 (owner: "o botão salvar não faz nada nem aviso de
 * erro"). Antes o editor mostrava só o PRIMEIRO issue, num toast no topo da
 * página, com o caminho do input (`br_fiscal.municipal_registration`) e o texto
 * cru do Zod em inglês ("Too small: expected string…"). Quem clicava no Salvar
 * do rodapé não via o toast, e um segundo clique com o mesmo erro não mostrava
 * nada (o efeito só disparava quando o texto mudava).
 *
 * Contrato: o caminho do input vira a chave do formulário — último segmento
 * para `address.*`/`br_fiscal.*`/raiz, e `nfse_<modo>.<campo>` para
 * `br_fiscal.nfse.<modo>.…` (as alíquotas, `taxes.iss` etc., viram o nome da
 * alíquota). `too_small`/`invalid_type` em campo vazio = obrigatório.
 *
 * Usado por: `SellerEntityEditorPage` (errorText por campo e resumo junto ao Salvar).
 */
export function fieldIssues(
  issues: readonly { path: readonly PropertyKey[]; code: string; message: string }[],
): FieldIssue[] {
  return issues.map((i) => {
    const path = i.path.map(String);
    const nfse = path[0] === 'br_fiscal' && path[1] === 'nfse' && path[2] !== undefined;
    const last = path[path.length - 1] ?? '';
    const field = nfse ? `nfse_${path[2] ?? ''}.${last}` : last;
    const kind: FieldIssue['kind'] =
      i.code === 'custom'
        ? 'custom'
        : i.code === 'too_small' || i.code === 'invalid_type'
          ? 'required'
          : i.code === 'invalid_format' || i.code === 'invalid_string'
            ? 'format'
            : 'invalid';
    return { field, kind, message: i.message };
  });
}
